import StoreModuleService from '../StoreModuleService';
import { MODULE_NAME } from './const';
import state from './state';
import {
	SET_TASK_LOG,
	SET_SELECTED_TASK_INFO,
	SET_SELECTED_TASK_NODES,
	SET_NODE_RUN_DATA,
	LOADING_LOGS,
	LOADING_SELECTED_TASK_NODES,
	SET_SELECTED_TASK_ID,
	SET_ANALYTICS_RUNS_BY_DAY,
	SET_ANALYTICS_RUNS_BY_USER,
	SET_ANALYTICS_RUNS_BY_FEATURE,
	SET_ANALYTICS_LATEST_ERRORS,
	SET_PERSONAL_INTERFACES_TASK_LOG,
} from './mutations';
import api from '@/api';
import { selectedTaskId } from './getters';
import { isNullOrUndefined } from '@/utils/helpers';

const buildAnalyticsActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const getTaskLog = buildAnalyticsActionObject('getTaskLog');
export const getPersonalInterfacesTaskLog = buildAnalyticsActionObject('getPersonalInterfacesTaskLog');
export const getNodeRunData = buildAnalyticsActionObject('getNodeRunData');
export const fetchNodeRunData = buildAnalyticsActionObject('fetchNodeRunData');
export const getTaskNodes = buildAnalyticsActionObject('getTaskNodes');
export const fetchTaskInfo = buildAnalyticsActionObject('getTaskInfo');
export const loadTask = buildAnalyticsActionObject('loadSelectedTask');
export const clearTask = buildAnalyticsActionObject('clearTask');
export const getAnalyticsRunsByDay = buildAnalyticsActionObject('getAnalyticsRunsByDay');
export const getAnalyticsRunsByUser = buildAnalyticsActionObject('getAnalyticsRunsByUser');
export const getAnalyticsRunsByFeature = buildAnalyticsActionObject('getAnalyticsRunsByFeature');
export const getAnalyticsLatestErrors = buildAnalyticsActionObject('getAnalyticsLatestErrors');
export const clearAnalyticsState = buildAnalyticsActionObject('clearAnalyticsState');

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [getTaskLog.action]({ commit }, params) {
		commit(LOADING_LOGS, true);
		try {
			const ret = await api.getTaskLog(params);
			if (params.start && params.start > 0) {
				commit(SET_TASK_LOG, [...state.taskLog, ...ret]);
			} else {
				commit(SET_TASK_LOG, ret);
			}
			return ret;
		} finally {
			commit(LOADING_LOGS, false);
		}
	},
	async [getPersonalInterfacesTaskLog.action]({ commit }, { taskLogId }) {
		const ret = await api.getPersonalInterfacesTaskLog(taskLogId);
		commit(SET_PERSONAL_INTERFACES_TASK_LOG, { taskId: taskLogId, taskDetails: ret });
	},

	async [getNodeRunData.action]({ dispatch }, payload) {
		const { nodeRunId, forceGet } = payload;
		const dataIsMissing = isNullOrUndefined(state.nodeRunData[nodeRunId]);
		const needToGet = dataIsMissing || forceGet;
		if (needToGet) {
			await dispatch(fetchNodeRunData.action, { nodeRunId });
		}
	},
	async [fetchNodeRunData.action]({ commit }, payload) {
		const { nodeRunId } = payload;
		const ret = await api.getNodeRunData(nodeRunId);
		commit(SET_NODE_RUN_DATA, { nodeRunId, taskLog: ret });
		return ret;
	},
	async [getTaskNodes.action]({ commit }, taskID) {
		commit(LOADING_SELECTED_TASK_NODES, true);
		const ret = await api.getTaskNodes(taskID);
		commit(SET_SELECTED_TASK_NODES, ret);
		commit(LOADING_SELECTED_TASK_NODES, false);
	},
	async [fetchTaskInfo.action]({ commit }, taskID) {
		commit(LOADING_SELECTED_TASK_NODES, true);
		const { flow, nodes, outputs } = await api.getTaskInfo(taskID);
		commit(SET_SELECTED_TASK_INFO, flow);
		commit(SET_SELECTED_TASK_NODES, nodes);
		for (const [nodeRunId, output] of Object.entries(outputs)) {
			commit(SET_NODE_RUN_DATA, { nodeRunId, taskLog: output });
		}
		commit(LOADING_SELECTED_TASK_NODES, false);
	},
	async [loadTask.action]({ commit, dispatch, getters }, taskID) {
		commit(LOADING_SELECTED_TASK_NODES, true);
		commit(SET_SELECTED_TASK_ID, taskID);
		await dispatch(fetchTaskInfo.action, getters[selectedTaskId.getter]);
		commit(LOADING_SELECTED_TASK_NODES, false);
	},
	[clearTask.action]({ commit }) {
		commit(SET_SELECTED_TASK_ID, null);
		commit(SET_SELECTED_TASK_INFO, null);
	},
	async [getAnalyticsRunsByDay.action]({ commit }, payload = {}) {
		const ret = await api.getAnalyticsRunsByDay(payload.spaceId, payload.includeAllSpaces, payload.dateRangeGTE, payload.dateRangeLTE);
		commit(SET_ANALYTICS_RUNS_BY_DAY, ret);
		return ret;
	},
	async [getAnalyticsLatestErrors.action]({ commit }, payload = {}) {
		const ret = await api.getAnalyticsLatestErrors(payload.spaceId, payload.includeAllSpaces, payload.dateRangeGTE, payload.dateRangeLTE);
		commit(SET_ANALYTICS_LATEST_ERRORS, ret);
		return ret;
	},
	async [getAnalyticsRunsByUser.action]({ commit }, payload = {}) {
		const ret = await api.getAnalyticsRunsByUser(
			payload.spaceId,
			payload.limit,
			payload.includeAllSpaces,
			payload.dateRangeGTE,
			payload.dateRangeLTE
		);
		commit(SET_ANALYTICS_RUNS_BY_USER, ret);
		return ret;
	},
	async [getAnalyticsRunsByFeature.action]({ commit }, payload = {}) {
		const ret = await api.getAnalyticsRunsByFeature(
			payload.spaceId,
			payload.limit,
			payload.includeAllSpaces,
			payload.dateRangeGTE,
			payload.dateRangeLTE
		);
		commit(SET_ANALYTICS_RUNS_BY_FEATURE, ret);
		return ret;
	},
	async [clearAnalyticsState.action]({ commit }) {
		commit(SET_ANALYTICS_RUNS_BY_DAY, {});
		commit(SET_ANALYTICS_LATEST_ERRORS, []);
		commit(SET_ANALYTICS_RUNS_BY_USER, []);
		commit(SET_ANALYTICS_RUNS_BY_FEATURE, []);
	},
};

export default actions;
